import React from 'react';
import './cabinet/styles/cabinet.css'; // Global tokens and base styles
import { createRoot } from 'react-dom/client';
import { I18nProvider } from './cabinet/context/I18nContext';
import { AuthProvider } from './cabinet/context/AuthContext';
import { useAuth } from './cabinet/hooks/useAuth';
import { CabinetShell } from './cabinet/components/layout/CabinetShell';
import { ToastContainer } from './cabinet/components/ui/Toast';
import { FullscreenLoader } from './cabinet/components/ui/FullscreenLoader';
import { usePwaAnalytics } from './cabinet/hooks/usePwaAnalytics';


import { getUrlParam } from './cabinet/utils/url';

// A wrapper to handle redirection if user is NOT logged in
const CabinetGuard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Check if we are on the postponement page via query param with valid hash
  const isPostponement = getUrlParam('tab') === 'przeloz' && getUrlParam('order_id') !== null && getUrlParam('hash') !== null;

  const isResetPassword = getUrlParam('action') === 'set_new_password';

  if (isResetPassword) {
    return null; // Let AuthApp handle the password reset form
  }

  if (isPostponement) {
    return (
      <div className="react-cabinet-isolated">
        <CabinetShell />
        <ToastContainer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="react-cabinet-isolated">
        <FullscreenLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (document.getElementById('react-auth')) return null;
    
    if (typeof window !== 'undefined' && window.cabinetVars) {
      const cabinetVarsObj = window.cabinetVars as NonNullable<typeof window.cabinetVars>;
      const currentUrl = window.location.href.split('?')[0].replace(/\/$/, '');
      const loginUrl = cabinetVarsObj.loginUrl.split('?')[0].replace(/\/$/, '');
      
      // Normalize to prevent infinite redirect loops caused by trailing slashes
      if (currentUrl !== loginUrl && !currentUrl.includes('/logowanie') && !currentUrl.includes('/my-account') && !currentUrl.includes('/moje-konto')) {
        window.location.href = cabinetVarsObj.loginUrl;
      }
    }
    return null;
  }

  return (
    <div className="react-cabinet-isolated">
      <CabinetShell />
      <ToastContainer />
    </div>
  );
};

// Внутрішній компонент — рендериться всередині AuthProvider,
// тому може безпечно викликати useAuth().
const CabinetAppInner: React.FC = () => {
  const { user } = useAuth();
  // Передаємо user?.id щоб:
  // 1. setGa4UserId викликався ДО pwa_session_start (правильний порядок)
  // 2. pwa_session_start не стріляв для анонімних сесій без user_id
  usePwaAnalytics(user?.id);
  return <CabinetGuard />;
};

const CabinetApp: React.FC = () => {
  return (
    <I18nProvider>
      <AuthProvider>
        <CabinetAppInner />
      </AuthProvider>
    </I18nProvider>
  );
};

import { Sentry } from './sentry';

// Mount
const container = document.getElementById('react-cabinet');
if (container) {
  const root = createRoot(container);
  root.render(
    <Sentry.ErrorBoundary fallback={<p>Сталася помилка в кабінеті. Ми вже працюємо над цим.</p>}>
      <CabinetApp />
    </Sentry.ErrorBoundary>
  );
}

export default CabinetApp;
